import React from 'react';
import { Section, SectionMarker, Reveal, RevealHairline } from '../index';
import { APP_STORE_PROOF, hasRating, hasReviews } from '../appStoreProof';

/**
 * §05 ON THE RECORD — third-party reassurance at the moment of decision,
 * without inventing a single number.
 *
 * Ships in state A: the founders' attestation only. No rating, no review
 * count, no download count, no press logos, no "loved by thousands", and no
 * skeleton or "coming soon" standing in for absent data. The attestation is
 * not third-party proof — it is founder proof, it is 100% verified, and it
 * carries the section on its own.
 *
 * 137 days: January 18 2025 → June 4 2025.
 *   Jan 18→31 = 13 · Feb 28 · Mar 31 · Apr 30 · May 31 · Jun 4
 *   13 + 28 + 31 + 30 + 31 + 4 = 137. Confirmed. Both endpoint dates are
 *   verified facts and the arithmetic is checkable; if it ever fails to
 *   compute to 137, drop the row rather than adjust the story.
 *
 * The attestation is set in MONO, not serif. Every instinct says a founder
 * quote should be serif — here it must not be. The line is evidence, the case
 * file's signed statement. Serif would make it sentimental; mono makes it
 * testimony. Never set it in serif and never sentence-case it.
 *
 * No CTA — the Final CTA is immediately below.
 */

function StatRow({ label, value, tone = 'paper', delay }) {
  const tones = { paper: 'text-paper', gold: 'text-gold-400', coral: 'text-coral' };
  return (
    <>
      <Reveal delay={delay}>
        <div className="flex flex-col items-start gap-1 pb-[10px] min-[421px]:flex-row min-[421px]:items-baseline">
          <span className="cp-mono text-[length:var(--fs-mono)]">{label}</span>
          <span
            aria-hidden="true"
            className="mx-3 hidden min-w-[24px] flex-1 -translate-y-[0.28em] border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
          />
          <span className={`cp-mono text-[length:var(--fs-mono-value)] ${tones[tone]}`}>
            {value}
          </span>
        </div>
      </Reveal>
      <RevealHairline delay={delay} className="mb-3" />
    </>
  );
}

/**
 * Stars carry three redundant signals — glyphs, the numeral, and the count.
 * A star row alone is a colour-and-shape-only affordance and fails the
 * colourblind gate by itself. Half-stars are not attempted: the glyph row
 * rounds down and the numeral carries the precision.
 */
function RatingBlock({ rating, ratingCount }) {
  const filled = Math.floor(rating);
  return (
    <div
      data-testid="record-rating"
      aria-label={`Rated ${rating} out of 5 from ${ratingCount} App Store ratings`}
      className="mb-8"
    >
      <p className="flex items-baseline gap-3">
        <span aria-hidden="true" className="text-gold-400 text-[18px] tracking-[0.1em]">
          {'★'.repeat(filled)}
          {'☆'.repeat(5 - filled)}
        </span>
        <span className="cp-poster text-gold-400 text-[32px] leading-none">
          {rating.toFixed(1)}
        </span>
      </p>
      <p className="cp-mono mt-2 text-[length:var(--fs-mono)]">
        App Store · {ratingCount} ratings
      </p>
    </div>
  );
}

export function Proof() {
  const showRating = hasRating(APP_STORE_PROOF);
  const showReviews = hasReviews(APP_STORE_PROOF);

  return (
    <Section
      id="record"
      labelledBy="record-h"
      tone="canvas"
      pad={false}
      className="py-[clamp(64px,8vw,104px)]"
    >
      {/* No visible heading by design; the section still needs an
          accessible name. */}
      <h2 id="record-h" className="sr-only">
        On the record
      </h2>
      <SectionMarker className="mb-8">§ 05 · On the record</SectionMarker>

      <div className="grid gap-10 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          {showRating && (
            <Reveal>
              <RatingBlock
                rating={APP_STORE_PROOF.rating}
                ratingCount={APP_STORE_PROOF.ratingCount}
              />
            </Reveal>
          )}

          <Reveal data-testid="record-attestation">
            {/* No quotation marks, no glow, no card. Just the gold rule and
                the void. */}
            <blockquote className="border-l-2 border-gold-400 pl-5">
              <p className="cp-mono max-w-[34ch] text-[length:clamp(0.9375rem,0.82rem+0.6vw,1.25rem)] leading-[1.6] tracking-[0.12em] text-paper">
                We crossed paths 460 times before we met. Different states. Six years.
              </p>
              <cite className="cp-mono mt-5 block not-italic text-[length:var(--fs-mono)]">
                — Nick &amp; Michelle, founders
              </cite>
            </blockquote>
          </Reveal>

          {showReviews && (
            <ul className="mt-10 grid gap-8 md:grid-cols-2">
              {APP_STORE_PROOF.reviews.slice(0, 3).map((r) => (
                <li key={r.author}>
                  <Reveal>
                    <p className="cp-story text-[length:var(--fs-serif)] text-paper-soft">
                      {r.quote}
                    </p>
                    <p className="cp-mono mt-3 text-[length:var(--fs-mono)]">— {r.author}</p>
                  </Reveal>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-4 lg:col-start-9">
          <StatRow label="Crossings found" value="460" tone="gold" delay={80} />
          <StatRow label="Years of near misses" value="Six" delay={170} />
          {/* "From meeting", not "from first date" — the facts doc says they
              MET on January 18 2025 and never says that date was a first
              date. The old label invented a fact. */}
          <StatRow label="Days from meeting to engaged" value="137" tone="coral" delay={260} />
        </div>
      </div>
    </Section>
  );
}

export default Proof;
